import React, { useCallback, useMemo, useState } from 'react';
import styles from './NewFooter.module.scss';
import AdminLoginModal from './AdminLoginModal';
import { Dropdown } from 'Components';
import { Menu } from 'antd';
import {
	HELP_DESK_LINK,
	PRIVACY_POLICY_LINK,
	TERMS_OF_USE_LINK
} from 'app-config';
import { TFunction } from 'i18next';
import { LANGUAGE_DROPDOWN } from 'Components/Sidebar/Siderbar.constant';
import { useStores } from 'Hooks/useStore';
import useLanguageChange from 'Components/Sidebar/hooks/useLanguageChange';
import { LanguageI18nKey } from 'Services/Api/Common/Types';
import { TITLE_BY_MENU_KEY } from 'i18n/i18n.constants';

interface FooterProps {
	isAdminLogin: boolean;
	setShowResetPassword?: React.Dispatch<React.SetStateAction<boolean>>;
	t: TFunction
}

const NewFooter: React.FC<FooterProps> = ({
	isAdminLogin,
	setShowResetPassword,
	t
}) => {
	const [showModal, setShowModal] = useState(false);
	const { authStore, languageStore } = useStores();
	const { handleLanguageChange } = useLanguageChange({ authStore, languageStore });

	const openUrl = useCallback((url: string) => window.open(url, '_blank'), []);
	const toggleModal = useCallback((show: boolean) => setShowModal(show), []);

	const contents = useMemo(
		() => [
			{ label: t('auth.layout.footer.contact'), onclick: () => openUrl(HELP_DESK_LINK) },
			{
				label: t('auth.layout.footer.terms'),
				onclick: () => openUrl(TERMS_OF_USE_LINK)
			},
			{ label: t('auth.layout.footer.privacy'), onclick: () => openUrl(PRIVACY_POLICY_LINK) },
			...(isAdminLogin
				? [
						{
							label: t('auth.layout.footer.admin'),
							onclick: () => toggleModal(true)
						}
				]
				: [])
		],
		[isAdminLogin, t]
	);

	return (
		<>
			<div className={styles.root}>
				{contents.map((el, index) => (
					<FooterItem
						key={index}
						label={el.label}
						onclick={el.onclick}
					/>
				))}
				<LanguageDropDown {...{ handleLanguageChange, languageCode: languageStore.language }}/>
			</div>
			{setShowResetPassword && (
				<AdminLoginModal
					visible={showModal}
					onCancel={() => toggleModal(false)}
					setShowResetPassword={setShowResetPassword}
				/>
			)}
		</>
	);
};

const LanguageDropDown = ({ handleLanguageChange, languageCode }: {
	handleLanguageChange: ({ key } : { key: string }) => void,
	languageCode: LanguageI18nKey
}) => {
	const menu = (
		<Menu style={{ borderRadius: '6px' }} selectedKeys={[languageCode]} onClick={(event) => handleLanguageChange({ key: event.key as string })}>
			{LANGUAGE_DROPDOWN.map(item => (<Menu.Item key={item.key}>{item.label}</Menu.Item>))}
		</Menu>
	);

	return (
		<Dropdown overlay={menu} className={styles.languageBtn}>
			{TITLE_BY_MENU_KEY[languageCode]}
		</Dropdown>
	);
};

interface FooterItemProps {
	label: string;
	onclick: () => void;
}

const FooterItem: React.FC<FooterItemProps> = ({ label, onclick }) => {
	return (
		<div className={styles.label} onClick={onclick}>
			{label}
		</div>
	);
};

export default NewFooter;
